/**
 * sortUtils — pure helpers for column sorting in the SimpleExcelViewer.
 *
 * The grid renders rows positionally (row index === both screen position
 * and the key for styles/merges/links/formulas).  To sort without
 * rewriting the whole render pipeline, we build a *permutation* of the
 * data rows and produce a derived model: the rows (and every per-row
 * keyed map) are re-ordered, while the row-number gutter stays 1..N — so
 * the data moves under fixed row numbers, exactly like Excel.
 *
 * Performance: callers memoize `computeSortOrder` on the rows-array
 * identity (which does NOT change on a column/row resize), so resizing
 * never recomputes the sort.  Only the cheap per-field remaps re-run when
 * their own source changes.
 */

// Coerce a display value to a number for numeric ordering.  Strips common
// currency / grouping / percent decoration so "$1,234" sorts as 1234.
function toNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v == null) return null;
  const s = String(v).trim().replace(/[$£€%\s]/g, "").replace(/,/g, "");
  if (s === "" || isNaN(Number(s))) return null;
  return Number(s);
}

// Compare two NON-blank values (blanks are handled by the caller so they
// can always sink to the bottom regardless of direction).  Excel order:
// numbers before text, numeric values numerically, text case-insensitively.
function compareNonBlank(a, b) {
  const an = toNumber(a);
  const bn = toNumber(b);
  if (an !== null && bn !== null) return an - bn;
  if (an !== null) return -1; // numbers before text
  if (bn !== null) return 1;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

const isBlank = (v) => v == null || v === "";

/**
 * Compute a stable sort permutation for the data range.
 *
 * Supports MULTI-LEVEL sort (Excel "Custom Sort"): rows are compared by
 * the first level, ties broken by the second, and so on.
 *
 * @param rows    full (padded) row matrix from the model
 * @param levels  ordered [{ col, dir }] — col is 0-based, dir "asc"|"desc"
 * @param headerRows  number of leading rows to keep fixed (0 or 1)
 * @param dataRowCount  real number of data rows (padding rows below stay put)
 * @returns { perm, invPerm, start, end } or null when there's nothing to sort.
 *          perm[displayRow]   = sourceRow shown there
 *          invPerm[sourceRow] = displayRow it moves to
 */
export function computeSortOrder(rows, { levels, headerRows, dataRowCount }) {
  if (!Array.isArray(rows) || !Array.isArray(levels)) return null;
  const keys = levels.filter((l) => l && l.col != null && (l.dir === "asc" || l.dir === "desc"));
  if (keys.length === 0) return null;

  const total = rows.length;
  const start = Math.min(Math.max(headerRows | 0, 0), total);
  const end = Math.min(dataRowCount != null ? dataRowCount : total, total);
  if (end - start <= 1) return null;

  const order = [];
  for (let r = start; r < end; r += 1) order.push(r);

  order.sort((ra, rb) => {
    for (let i = 0; i < keys.length; i += 1) {
      const { col, dir } = keys[i];
      const sign = dir === "desc" ? -1 : 1;
      const va = rows[ra] ? rows[ra][col] : "";
      const vb = rows[rb] ? rows[rb][col] : "";
      const aB = isBlank(va);
      const bB = isBlank(vb);
      if (aB && bB) continue;           // tie on this key → next key
      if (aB) return 1;                 // blanks always last (both dirs)
      if (bB) return -1;
      const c = compareNonBlank(va, vb);
      if (c !== 0) return sign * c;
      // equal on this key → fall through to next key
    }
    return ra - rb;                     // stable tie-break
  });

  const perm = new Array(total);
  for (let i = 0; i < total; i += 1) perm[i] = i;          // identity outside range
  for (let k = 0; k < order.length; k += 1) perm[start + k] = order[k];

  const invPerm = new Array(total);
  for (let d = 0; d < total; d += 1) invPerm[perm[d]] = d;

  return { perm, invPerm, start, end };
}

/** Re-order a full-length array by the permutation (out[d] = arr[perm[d]]). */
export function applyPerm(arr, perm) {
  if (!Array.isArray(arr) || !perm) return arr;
  const out = new Array(perm.length);
  for (let d = 0; d < perm.length; d += 1) {
    out[d] = arr[perm[d]];
  }
  return out;
}

/** Remap a map keyed "r:c" so each entry moves to its new display row. */
export function remapRowKeys(map, invPerm) {
  if (!map || !invPerm) return map;
  const out = {};
  for (const key in map) {
    const sep = key.indexOf(":");
    const r = parseInt(key.slice(0, sep), 10);
    const c = key.slice(sep + 1);
    const nr = invPerm[r] != null ? invPerm[r] : r;
    out[`${nr}:${c}`] = map[key];
  }
  return out;
}

/**
 * Remap merges under the permutation.  Multi-row merged regions cannot be
 * preserved once their rows are reordered (Excel itself refuses to sort
 * across unequal merges), so any merge that spans >1 row AND lies inside
 * the sorted range is dropped.  Single-row merges and merges entirely
 * outside the sorted range are remapped and kept.
 */
export function remapMerges(merges, invPerm, start, end) {
  if (!merges || !invPerm) return merges;

  const multiRowRows = new Set();
  for (const key in merges) {
    const val = merges[key];
    if (val && val.rowSpan > 1) {
      const r = parseInt(key.slice(0, key.indexOf(":")), 10);
      for (let rr = r; rr < r + val.rowSpan; rr += 1) multiRowRows.add(rr);
    }
  }

  const out = {};
  for (const key in merges) {
    const sep = key.indexOf(":");
    const r = parseInt(key.slice(0, sep), 10);
    const c = key.slice(sep + 1);
    if (r >= start && r < end && multiRowRows.has(r)) continue; // drop split merge
    const nr = invPerm[r] != null ? invPerm[r] : r;
    out[`${nr}:${c}`] = merges[key];
  }
  return out;
}
